// SIGN IN AGAIN: the answer of the ACCOUNT page to `reauth_required`
// (R2963, deployment.md §7.1).
//
// A registered account adds a sign-in method, or deletes itself with no
// password, only on a fresh session: a session that a sign-in opened a
// short time ago. A refusal shows one line and the SIGN IN AGAIN control.
// The control signs out and opens the landing screen on the address of
// the ACCOUNT page, so the next sign-in comes back to this page. The
// landing screen carries the section through a provider (`withSection`
// of `ui/landing.ts`).
//
// Two refusals arrive as an `ApiError`: the email form and the delete. The
// link of a provider is a navigation, so its refusal arrives on the page
// address as `signin=reauth_required`. `fast/entry.ts` reads the address
// and calls `markReauth`, and the SETTINGS panel takes the mark when it
// opens.

import { ApiError, CODE_REAUTH_REQUIRED, authLogout } from '../../../net/api';
import { clearAllTokens } from '../../../net/token';
import { hubSettings as S } from '../../../gen/strings';
import { note } from '../../options/kit';
import { button } from '../../choice/choice';
import { SECTION_PARAM, TAB_PARAM } from '../panel';

/** The press of the SIGN IN AGAIN control. */
export const REAUTH_ACT = 'reauth-go';

/** The address the next sign-in comes back to: the ACCOUNT page of
 *  SETTINGS. */
export const REAUTH_RETURN = `/?${TAB_PARAM}=settings&${SECTION_PARAM}=account`;

let marked = false;

/** Records that the page address carried `signin=reauth_required`. */
export function markReauth(): void {
  marked = true;
}

/** Answers the mark of `markReauth` once, and clears it. */
export function takeReauth(): boolean {
  const was = marked;
  marked = false;
  return was;
}

/** True for the refusal of a change that needs a fresh session. */
export function isReauth(err: unknown): boolean {
  return err instanceof ApiError && err.code === CODE_REAUTH_REQUIRED;
}

/** The line and the control. `busy` is true while the sign-out runs. */
export function reauthBlock(busy: boolean): string {
  return '<div class="c-inset c-reauth">' + note(S.reauth_lede, true)
    + button(busy ? S.reauth_busy : S.reauth_go, { act: REAUTH_ACT }, { variant: 'primary', disabled: busy })
    + '</div>';
}

/**
 * Signs out and opens the landing screen on the ACCOUNT page. The resume
 * tokens go with the session, as on the SIGN OUT of the hub. A sign-out
 * that fails rejects, and the caller says so: a reload with the session
 * still in the cookie would open the hub again.
 */
export async function signInAgain(go: (path: string) => void = (p) => location.assign(p)): Promise<void> {
  await authLogout();
  clearAllTokens();
  go(REAUTH_RETURN);
}
